import { getVerifyUser, postLoginUser, postSetPassword } from 'api/auth'
import { useAuthContext } from 'auth/authContext'
import Spinner from 'components/Spinner'
import useQuery from 'hooks/useQuery'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import Ui from 'ui'
import ErrorLabel from 'ui/atoms/ErrorLabel'
import { hasText, objectHasPropWithValue } from 'utils/helpers'
import {
  error,
  initial,
  isError,
  isInitial,
  isLoading,
  isSuccess,
  loading,
  success,
} from 'utils/remoteData'
import { newPasswordSchema, schemasValidate } from 'utils/schemas'

import AuthCard from './components/AuthCard'

const ResetPasswordSchema = {
  password: newPasswordSchema,
}

function SetPassword() {
  // FORM
  const [formValues, setFormValues] = React.useState({
    password: '',
  })
  const [formError, setFormError] = React.useState({
    password: false,
  })
  const [formErrorLabel, setFormErrorLabel] = React.useState({
    password: '',
  })
  const [hasFormError, setHasFormError] = React.useState(false)

  // USER EMAIL
  const [email, setEmail] = React.useState()

  // REQUESTS HANDLERS
  const [verifyUserRequest, setVerifyUserRequest] = React.useState(initial())
  const [passwordRequest, setPasswordRequest] = React.useState(initial())

  const history = useHistory()

  // ULR QUERY
  const query = useQuery()
  const code = query.get('code')

  const { handleRedirectCallback, clearRedirectPath } = useAuthContext()

  // CODE VALIDATION
  if (!code && isInitial(verifyUserRequest)) {
    setVerifyUserRequest(error('This link has expired'))
  }

  React.useEffect(() => {
    setHasFormError(objectHasPropWithValue(formError, true))
  }, [formError])

  React.useEffect(() => {
    async function fetchData() {
      if (code && isInitial(verifyUserRequest)) {
        try {
          setVerifyUserRequest(loading())
          const data = await getVerifyUser({ code })
          setVerifyUserRequest(success(data))
          setEmail(data.email)
        } catch (err) {
          setVerifyUserRequest(error(err.message))
        }
      }
    }
    fetchData()
  }, [code, history, verifyUserRequest])

  const onChange = (name) => (value) => {
    setFormValues((current) => ({ ...current, [name]: value }))
    formError[name] &&
      schemasValidate(
        [[name, value, ResetPasswordSchema[name]]],
        setFormError,
        setFormErrorLabel
      )
  }

  const login = React.useCallback(async () => {
    try {
      await postLoginUser({
        email,
        password: formValues.password,
      })
      clearRedirectPath()
      handleRedirectCallback()
      toast.success('Success: Logged in')
      history.push('/')
    } catch (err) {}
  }, [clearRedirectPath, email, formValues, history, handleRedirectCallback])

  const onSubmit = React.useCallback(async () => {
    const { password } = formValues
    const isValid = await schemasValidate(
      [['password', password, ResetPasswordSchema.password]],
      setFormError,
      setFormErrorLabel
    )
    if (!isValid) {
      return
    }
    if (!hasText(email)) {
      return
    }
    try {
      setPasswordRequest(loading())
      const response = await postSetPassword({
        email,
        password: formValues.password,
        code,
      })
      setPasswordRequest(success(response))
      toast.success('Success: Password set. Logging you in...')
      login()
    } catch (err) {
      setPasswordRequest(error(err.message))
    }
  }, [code, email, formValues, login])

  return (
    <>
      <Helmet>
        <title>Set your Password/Zentake</title>
      </Helmet>

      <AuthCard>
        {isLoading(verifyUserRequest) && (
          <div className="flex flex-1 justify-center mt-3">
            <Spinner color="onLight" size="56" />
          </div>
        )}
        {isSuccess(verifyUserRequest) && (
          <>
            <h1 className="text-xl font-medium text-gray-700">
              Set your Password
            </h1>
            <div className="mt-6">
              <Ui.PasswordInput
                errorLabel={formErrorLabel.password}
                hasError={formError.password}
                label="Password"
                name="password"
                onChange={onChange('password')}
                type="password"
                value={formValues.password}
              />
            </div>
            <div className="grid mt-6">
              <Ui.PrimaryButton
                disabled={hasFormError}
                loading={isLoading(passwordRequest)}
                onClick={onSubmit}
              >
                Set
              </Ui.PrimaryButton>
            </div>
            {isError(passwordRequest) && (
              <div className="flex flex-1 justify-center mt-3">
                <ErrorLabel>{passwordRequest.error}</ErrorLabel>
              </div>
            )}
            <p className="mb-2 mt-1 text-xs text-gray-600 justify-self-center text-center">
              By clicking this button, you agree to our{' '}
              <a
                className="underline"
                href="https://www.zentake.com/terms-of-service"
                rel="noopener noreferrer"
                target="_blank"
              >
                Terms
              </a>{' '}
              and{' '}
              <a
                className="underline"
                href="https://www.zentake.com/privacy-policy"
                rel="noopener noreferrer"
                target="_blank"
              >
                Privacy Policy
              </a>
              .
            </p>
          </>
        )}
        {isError(verifyUserRequest) && (
          <div className="flex flex-1 justify-center">
            <ErrorLabel>{verifyUserRequest.error}</ErrorLabel>
          </div>
        )}
      </AuthCard>
    </>
  )
}

export default SetPassword
